import { useEffect, useRef } from 'react';

// ─── ONBOARDING MODAL ─────────────────────────────────────────────────────────
//
//  Shown once to new users immediately after their first login.
//  Calls onComplete() when the primary CTA is clicked (→ Dashboard + create modal).
//  Calls onExplore() when the secondary link is clicked (→ Feed).
//  Both paths call the /onboarded endpoint so the modal never reappears.

const FEATURES = [
  {
    icon: '🗂️',
    title: 'Ten beautiful layouts',
    desc: 'Horizontal, vertical, zigzag, map, newspaper, and more — pick the style that fits your story.',
  },
  {
    icon: '🌍',
    title: 'Share with the world',
    desc: 'Publish your timeline to the community feed, embed it anywhere, or keep it private.',
  },
  {
    icon: '🤝',
    title: 'Collaborate in real time',
    desc: 'Invite editors to build timelines together — great for teams, classes, and projects.',
  },
];

const OnboardingModal = ({ user, onComplete, onExplore }) => {
  const overlayRef  = useRef(null);
  const primaryRef  = useRef(null);
  const firstName   = (user?.name ?? 'there').split(' ')[0];

  // Focus the primary button when the modal opens.
  useEffect(() => {
    const raf = requestAnimationFrame(() => primaryRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  // Escape key closes (explore path).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onExplore(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onExplore]);

  // Trap focus within the modal.
  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      overlayRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) ?? []
    ).filter(el => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  };

  return (
    <div
      className="ob-overlay"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ob-title"
      onKeyDown={onKeyDown}
    >
      <div className="ob-panel">

        {/* ── Illustration ───────────────────────────────────────── */}
        <div className="ob-hero" aria-hidden="true">
          <div className="ob-hero-timeline">
            {['📅', '⭐', '🎯', '🚀'].map((icon, i) => (
              <div key={i} className="ob-hero-node" style={{ animationDelay: `${i * 0.15}s` }}>
                <span>{icon}</span>
              </div>
            ))}
            <div className="ob-hero-line" />
          </div>
        </div>

        {/* ── Headline ───────────────────────────────────────────── */}
        <h1 id="ob-title" className="ob-headline">
          Welcome to Chronos,&nbsp;{firstName}!
        </h1>
        <p className="ob-tagline">
          Build beautiful interactive timelines and share them with the world.
        </p>

        {/* ── Feature highlights ─────────────────────────────────── */}
        <ul className="ob-features" aria-label="Key features">
          {FEATURES.map(({ icon, title, desc }) => (
            <li key={title} className="ob-feature">
              <span className="ob-feature-icon" aria-hidden="true">{icon}</span>
              <div>
                <strong className="ob-feature-title">{title}</strong>
                <span className="ob-feature-desc">{desc}</span>
              </div>
            </li>
          ))}
        </ul>

        {/* ── CTAs ───────────────────────────────────────────────── */}
        <button
          ref={primaryRef}
          className="btn-primary ob-cta-primary"
          onClick={onComplete}
        >
          Create My First Timeline
        </button>

        <button
          className="ob-cta-secondary"
          onClick={onExplore}
        >
          Explore the Feed First
        </button>
      </div>
    </div>
  );
};

export default OnboardingModal;
